import { useState, useEffect } from "react";
import { P, FF, inputBase } from "../../styles/tokens";
import { Card } from "../ui";
import { api } from "../../services/api";
import { fmt } from "../../utils/format";
import { useAuth } from "../../contexts/AuthContext";
import { useFinancial } from "../../contexts/FinancialContext";
import { useNavigation } from "../../contexts/NavigationContext";
import PresupuestosView from "./PresupuestosView";
import RecurrentesView from "./RecurrentesView";
import NotificationsConfig from "./NotificationsConfig";
import SubscriptionConfig from "./SubscriptionConfig";
import AdminPanel from "./AdminPanel";

/* ── Iconos disponibles para categorías ── */
const ICON_OPTIONS = ["🍔","👗","🎮","💊","✈️","🏠","🚗","📚","💻","🍽️","🐾","🏋️","💅","📱","📦","🎵","🎬","💰","🎁","☕","🍕","🏥","🎓","🛒","💼","🔧","🌱","🐶","🎨","📷"];
const COLOR_OPTIONS = ["#F97316","#EC4899","#8B5CF6","#10B981","#3B82F6","#6366F1","#F59E0B","#14B8A6","#64748B","#EF4444","#84CC16","#06B6D4","#F43F5E","#A855F7","#94A3B8","#D946EF","#0EA5E9","#F472B6"];
const TIPO_LABELS = { efectivo: "💵 Efectivo", "débito": "💳 Débito", "crédito": "💳 Crédito", vales: "🎫 Vales", ahorro: "🐿️ Ahorro" };
const TIPO_OPTIONS = ["efectivo", "débito", "crédito", "vales", "ahorro"];
const BANCOS = ["BBVA", "Banorte", "Santander", "HSBC", "Citibanamex", "Scotiabank", "Inbursa", "BanRegio", "Azteca", "Nu", "Hey Banco", "Rappi", "Stori", "Otro"];

/* ═══════════════════════════════════════════════════════════════════
   ConfigView — Configuración principal con 5 tabs
   ═══════════════════════════════════════════════════════════════════ */
export default function ConfigView({
  cuentas, createCuenta, updateCuenta, deleteCuenta,
  categorias, createCategoria, updateCategoria, deleteCategoria,
  presupuestos, setPresupuestos, recurrentes, setRecurrentes,
  data, activeMonth,
  diaCobro, setDiaCobro,
  computedSaldos, refetchTransferencias,
  mobile,
}) {
  const [tab, setTab] = useState("cuentas");
  const [isAdmin, setIsAdmin] = useState(false);

  // Detectar si el usuario es admin de forma silenciosa
  useEffect(() => {
    api.admin.listUsers({ limit: 1, offset: 0 })
      .then(() => setIsAdmin(true))
      .catch(() => setIsAdmin(false));
  }, []);

  const tabStyle = (active) => ({
    flex: 1,
    minWidth: 0,
    padding: mobile ? "10px 4px" : "10px 12px",
    border: "none",
    borderRadius: 10,
    background: active ? P.ingresos.base : "transparent",
    color: active ? "#fff" : P.textSm,
    fontFamily: FF,
    fontSize: mobile ? 11 : 12,
    fontWeight: active ? 800 : 600,
    cursor: "pointer",
    transition: "all .15s",
    whiteSpace: "nowrap",
    display: "flex",
    flexDirection: mobile ? "column" : "row",
    alignItems: "center",
    justifyContent: "center",
    gap: mobile ? 2 : 6,
    overflow: "hidden",
    textOverflow: "ellipsis",
  });

  // En mobile: solo el icono + label corto. En desktop: icono + label completo.
  const TABS = [
    { key: "cuentas",      icon: "💳", label: "Cuentas",      shortLabel: "Cuentas" },
    { key: "categorias",   icon: "🏷️", label: "Categorías",   shortLabel: "Cat." },
    { key: "presupuestos", icon: "📊", label: "Presupuestos", shortLabel: "Presup." },
    { key: "recurrentes",  icon: "🔁", label: "Recurrentes",  shortLabel: "Recurr." },
    { key: "perfil",       icon: "👤", label: "Perfil",       shortLabel: "Perfil" },
    { key: "suscripcion",  icon: "⭐", label: "Suscripción",  shortLabel: "Plan" },
    ...(isAdmin ? [{ key: "admin", icon: "🛡️", label: "Admin", shortLabel: "Admin" }] : []),
  ];

  return (
    <div>
      {/* Tab Selector — sin scroll horizontal: 5 columnas iguales en mobile y desktop */}
      <Card style={{ padding: 4, display: "flex", gap: 3, marginBottom: 16, width: "100%" }}>
        {TABS.map(({ key, icon, label, shortLabel }) => (
          <button key={key} style={tabStyle(tab === key)} onClick={() => setTab(key)}>
            <span style={{ fontSize: mobile ? 16 : 13 }}>{icon}</span>
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {mobile ? shortLabel : label}
            </span>
          </button>
        ))}
      </Card>

      {tab === "cuentas" && (
        <CuentasSection
          cuentas={cuentas}
          createCuenta={createCuenta}
          updateCuenta={updateCuenta}
          deleteCuenta={deleteCuenta}
          computedSaldos={computedSaldos}
          refetchTransferencias={refetchTransferencias}
          mobile={mobile}
        />
      )}
      {tab === "categorias" && (
        <CategoriasSection
          categorias={categorias}
          createCategoria={createCategoria}
          updateCategoria={updateCategoria}
          deleteCategoria={deleteCategoria}
          mobile={mobile}
        />
      )}
      {tab === "presupuestos" && (
        <PresupuestosView
          presupuestos={presupuestos}
          setPresupuestos={setPresupuestos}
          data={data}
          activeMonth={activeMonth}
          categorias={categorias}
        />
      )}
      {tab === "recurrentes" && (
        <RecurrentesView
          recurrentes={recurrentes}
          setRecurrentes={setRecurrentes}
          cuentas={cuentas}
        />
      )}
      {tab === "perfil" && (
        <PerfilSection
          diaCobro={diaCobro}
          setDiaCobro={setDiaCobro}
        />
      )}
      {tab === "suscripcion" && (
        <SubscriptionConfig />
      )}
      {tab === "admin" && isAdmin && (
        <AdminPanel />
      )}
    </div>
  );
}

/* ── AccountForm — reutilizable para crear y editar inline ── */
function AccountForm({ form, setForm, editId, handleSave, onCancel, dayOptions, saving, formError }) {
  return (
    <Card style={{ padding: 16, border: `2px solid ${P.ingresos.tint}` }}>
      <div style={{ fontSize: 14, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 12 }}>
        {editId ? "Editar cuenta" : "Nueva cuenta"}
      </div>

      {/* Error inline */}
      {formError && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#DC2626", fontFamily: FF, fontWeight: 600 }}>
          {formError}
        </div>
      )}

      <FormField
        label="Nombre"
        htmlFor="cuenta-nombre"
        value={form.nombre}
        onChange={(v) => setForm({ ...form, nombre: v })}
        placeholder="Ej: BBVA Nómina"
      />

      <div style={{ marginBottom: 10 }}>
        <label htmlFor="cuenta-tipo" style={labelStyle}>Tipo</label>
        <div id="cuenta-tipo" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TIPO_OPTIONS.map((t) => (
            <button type="button" key={t} onClick={() => setForm({ ...form, tipo: t })}
              style={{ padding: "7px 12px", borderRadius: 10, border: `1.5px solid ${form.tipo === t ? P.ingresos.base : P.border}`, background: form.tipo === t ? P.ingresos.pastel : P.bg, color: form.tipo === t ? P.ingresos.base : P.textSm, fontFamily: FF, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
              {TIPO_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      {(form.tipo === "débito" || form.tipo === "crédito") && (
        <div style={{ marginBottom: 10 }}>
          <label htmlFor="cuenta-banco" style={labelStyle}>Banco</label>
          <select id="cuenta-banco" value={form.banco} onChange={(e) => setForm({ ...form, banco: e.target.value })} style={{ ...inputBase, appearance: "auto" }}>
            <option value="">Seleccionar...</option>
            {BANCOS.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <FormField
            label={form.tipo === "crédito" ? "Saldo usado actualmente" : "Saldo inicial"}
            htmlFor="cuenta-saldo"
            value={form.saldo_inicial}
            onChange={(v) => setForm({ ...form, saldo_inicial: v })}
            placeholder="0"
            inputMode="decimal"
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: P.textSm, fontFamily: FF, fontWeight: 600, paddingBottom: 10, whiteSpace: "nowrap", cursor: "pointer" }}>
          <input type="checkbox" checked={form.rastrear_saldo} onChange={(e) => setForm({ ...form, rastrear_saldo: e.target.checked })} />
          Incluir en mi saldo
        </label>
      </div>

      {form.tipo === "crédito" && (
        <div style={{ background: P.bg, borderRadius: 12, padding: 12, marginBottom: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: P.ingresos.base, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, fontFamily: FF }}>Datos de tarjeta de crédito</div>
          <FormField label="Límite de crédito" htmlFor="cuenta-limite" value={form.limite_credito} onChange={(v) => setForm({ ...form, limite_credito: v })} placeholder="50000" inputMode="decimal" />
          <div style={{ display: "flex", gap: 10 }}>
            <div style={{ flex: 1, marginBottom: 10 }}>
              <label htmlFor="cuenta-corte" style={labelStyle}>Día de corte</label>
              <select id="cuenta-corte" value={form.fecha_corte} onChange={(e) => setForm({ ...form, fecha_corte: e.target.value })} style={{ ...inputBase, appearance: "auto" }}>
                <option value="">--</option>
                {dayOptions.map((d) => <option key={d} value={d}>Día {d}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, marginBottom: 10 }}>
              <label htmlFor="cuenta-pago" style={labelStyle}>Día límite de pago</label>
              <select id="cuenta-pago" value={form.fecha_limite_pago} onChange={(e) => setForm({ ...form, fecha_limite_pago: e.target.value })} style={{ ...inputBase, appearance: "auto" }}>
                <option value="">--</option>
                {dayOptions.map((d) => <option key={d} value={d}>Día {d}</option>)}
              </select>
            </div>
          </div>
          {form.fecha_corte && form.fecha_limite_pago && (
            <div style={{ padding: "8px 12px", background: "#FFFBEB", borderRadius: 10, border: "1px solid #FDE68A", marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: "#92400E", fontFamily: FF, lineHeight: 1.5 }}>
                📋 <strong>Tu ciclo:</strong> Las compras del día {Number(form.fecha_corte) + 1 > 31 ? 1 : Number(form.fecha_corte) + 1} al día {form.fecha_corte} se pagan antes del <strong>día {form.fecha_limite_pago} {Number(form.fecha_limite_pago) < Number(form.fecha_corte) ? "del mes siguiente" : "del mismo mes"}</strong>.
              </div>
            </div>
          )}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button type="button" onClick={handleSave} disabled={saving}
          style={{ flex: 1, padding: "10px 16px", borderRadius: 12, border: "none", background: P.ingresos.base, color: "#fff", fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}>
          {saving ? "Guardando..." : editId ? "Guardar cambios" : "Agregar"}
        </button>
        <button type="button" onClick={onCancel}
          style={{ padding: "10px 16px", borderRadius: 12, border: `1.5px solid ${P.border}`, background: "transparent", color: P.textSm, fontFamily: FF, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Cancelar
        </button>
      </div>
    </Card>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CuentasSection — CRUD de cuentas / métodos de pago
   ═══════════════════════════════════════════════════════════════════ */
function CuentasSection({ cuentas, createCuenta, updateCuenta, deleteCuenta, computedSaldos, refetchTransferencias, mobile }) {
  const [showForm, setShowForm]     = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState(emptyAccountForm());
  const [saving, setSaving]         = useState(false);
  const [formError, setFormError]   = useState("");
  const [sectionError, setSectionError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // id a eliminar
  const [showTransfer, setShowTransfer]   = useState(false);
  const [transfer, setTransfer]     = useState({ origen: "", destino: "", monto: "", concepto: "Retiro" });
  const [transferOk, setTransferOk] = useState(false);

  // Saldos desde computedSaldos (FinancialContext), NO desde c.saldo_actual.
  // saldo_actual es el espejo que persiste el backend; leerlo aquí hacía que
  // Configuración mostrara cifras distintas al Resumen para el mismo usuario.
  const saldoDe = (c) => Number(computedSaldos?.[c.id]) || 0;
  const totalPorTipo = (tipo) =>
    cuentas.filter(c => c.tipo === tipo && c.rastrear_saldo).reduce((s, c) => s + saldoDe(c), 0);

  const efectivoTotal      = totalPorTipo("efectivo");
  const debitoTotal        = totalPorTipo("débito");
  const ahorroTotal        = totalPorTipo("ahorro");
  const valesTotal         = totalPorTipo("vales");
  const creditoDisponible  = cuentas.filter(c => c.tipo === "crédito").reduce((s, c) => s + ((Number(c.limite_credito) || 0) - saldoDe(c)), 0);
  const disponiblePrincipal = efectivoTotal + debitoTotal + ahorroTotal + valesTotal;

  function showSectionError(msg) {
    setSectionError(msg);
    setTimeout(() => setSectionError(""), 3000);
  }

  const handleTransfer = async () => {
    if (!transfer.origen || !transfer.destino || !transfer.monto) return;
    try {
      await api.transferencias.create({
        cuenta_origen:  transfer.origen,
        cuenta_destino: transfer.destino,
        monto:    Number(transfer.monto),
        concepto: transfer.concepto || "Transferencia",
      });
      // No se escribe saldo_actual a mano: era una tercera fórmula calculada
      // desde estado stale que competía con la del backend. Basta con refrescar
      // las transferencias — computedSaldos deriva el saldo de ellas.
      await refetchTransferencias?.();
      setShowTransfer(false);
      setTransfer({ origen: "", destino: "", monto: "", concepto: "Retiro" });
      setTransferOk(true);
      setTimeout(() => setTransferOk(false), 2000);
    } catch (e) {
      showSectionError("Error al transferir: " + (e.message || "intenta de nuevo"));
    }
  };

  function emptyAccountForm() {
    return { nombre: "", tipo: "débito", banco: "", saldo_inicial: "", rastrear_saldo: false, limite_credito: "", fecha_corte: "", fecha_limite_pago: "" };
  }

  const handleEdit = (cuenta) => {
    setForm({
      nombre:            cuenta.nombre || "",
      tipo:              cuenta.tipo || "débito",
      banco:             cuenta.banco || "",
      saldo_inicial:     cuenta.saldo_inicial != null ? String(cuenta.saldo_inicial) : "",
      rastrear_saldo:    cuenta.rastrear_saldo || false,
      limite_credito:    cuenta.limite_credito != null ? String(cuenta.limite_credito) : "",
      fecha_corte:       cuenta.fecha_corte != null ? String(cuenta.fecha_corte) : "",
      fecha_limite_pago: cuenta.fecha_limite_pago != null ? String(cuenta.fecha_limite_pago) : "",
    });
    setEditId(cuenta.id);
    setFormError("");
    setShowForm(true);
  };

  const handleSave = async () => {
    // Validación
    if (!form.nombre.trim()) {
      setFormError("El nombre es obligatorio");
      return;
    }
    setFormError("");

    const payload = {
      nombre:        form.nombre.trim(),
      tipo:          form.tipo,
      banco:         form.banco || null,
      rastrear_saldo: form.rastrear_saldo,
      saldo_inicial: form.saldo_inicial ? Number(form.saldo_inicial) : null,
    };
    if (form.tipo === "crédito") {
      payload.limite_credito    = form.limite_credito    ? Number(form.limite_credito)    : null;
      payload.fecha_corte       = form.fecha_corte       ? Number(form.fecha_corte)       : null;
      payload.fecha_limite_pago = form.fecha_limite_pago ? Number(form.fecha_limite_pago) : null;
    }

    setSaving(true);
    try {
      if (editId) {
        await updateCuenta(editId, payload);
      } else {
        await createCuenta(payload);
      }
      setShowForm(false);
      setEditId(null);
      setForm(emptyAccountForm());
    } catch (e) {
      showSectionError("Error al guardar: " + (e.message || "intenta de nuevo"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCuenta(id);
      setConfirmDelete(null);
    } catch (e) {
      showSectionError("Error al eliminar: " + (e.message || "intenta de nuevo"));
      setConfirmDelete(null);
    }
  };

  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Error de sección */}
      {sectionError && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#DC2626", fontFamily: FF, fontWeight: 600 }}>
          {sectionError}
        </div>
      )}

      {/* Transfer OK feedback */}
      {transferOk && (
        <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#16A34A", fontFamily: FF, fontWeight: 700 }}>
          ✓ Transferencia realizada
        </div>
      )}

      {/* Balance Summary */}
      {cuentas.some(c => c.rastrear_saldo) && (
        <Card style={{ padding: "16px 18px", background: "#143D2E" }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6, fontFamily: FF }}>Total disponible</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#fff", fontFamily: FF, marginBottom: 10 }}>{fmt(disponiblePrincipal)}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {efectivoTotal     > 0 && <MiniStatDark label="Efectivo"      value={fmt(efectivoTotal)} />}
            {debitoTotal       > 0 && <MiniStatDark label="Débito"        value={fmt(debitoTotal)} />}
            {ahorroTotal       > 0 && <MiniStatDark label="Ahorro"        value={fmt(ahorroTotal)} />}
            {valesTotal        > 0 && <MiniStatDark label="Vales"         value={fmt(valesTotal)} />}
            {creditoDisponible > 0 && <MiniStatDark label="Crédito disp." value={fmt(creditoDisponible)} />}
          </div>
        </Card>
      )}

      {/* Transfer button */}
      {cuentas.length >= 2 && !showTransfer && (
        <button onClick={() => setShowTransfer(true)}
          style={{ padding: "10px 16px", borderRadius: 12, border: `1.5px solid ${P.ingresos.tint}`, background: P.ingresos.pastel, color: P.ingresos.base, fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          🔄 Mover dinero entre cuentas
        </button>
      )}

      {/* Transfer form */}
      {showTransfer && (
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 10 }}>Mover dinero</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label htmlFor="transfer-de" style={labelStyle}>De</label>
              <select id="transfer-de" value={transfer.origen} onChange={(e) => setTransfer({ ...transfer, origen: e.target.value })} style={{ ...inputBase, appearance: "auto" }}>
                <option value="">Seleccionar...</option>
                {cuentas.map(c => <option key={c.id} value={c.id}>{TIPO_LABELS[c.tipo]?.split(" ")[0]} {c.nombre}</option>)}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 140 }}>
              <label htmlFor="transfer-a" style={labelStyle}>A</label>
              <select id="transfer-a" value={transfer.destino} onChange={(e) => setTransfer({ ...transfer, destino: e.target.value })} style={{ ...inputBase, appearance: "auto" }}>
                <option value="">Seleccionar...</option>
                {cuentas.filter(c => c.id !== transfer.origen).map(c => <option key={c.id} value={c.id}>{TIPO_LABELS[c.tipo]?.split(" ")[0]} {c.nombre}</option>)}
              </select>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <div style={{ flex: 1 }}>
              <FormField label="Concepto" htmlFor="transfer-concepto" value={transfer.concepto} onChange={(v) => setTransfer({ ...transfer, concepto: v })} placeholder="Retiro ATM" />
            </div>
            <div style={{ width: 120 }}>
              <FormField label="Monto" htmlFor="transfer-monto" value={transfer.monto} onChange={(v) => setTransfer({ ...transfer, monto: v })} placeholder="0.00" inputMode="decimal" />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleTransfer}
              style={{ flex: 1, padding: "10px 16px", borderRadius: 12, border: "none", background: P.ingresos.base, color: "#fff", fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              Transferir
            </button>
            <button onClick={() => setShowTransfer(false)}
              style={{ padding: "10px 16px", borderRadius: 12, border: `1.5px solid ${P.border}`, background: "transparent", color: P.textSm, fontFamily: FF, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
              Cancelar
            </button>
          </div>
        </Card>
      )}

      {/* Lista de cuentas */}
      {cuentas.map((c) => (
        editId === c.id && showForm ? (
          <AccountForm
            key={c.id}
            form={form} setForm={setForm}
            editId={editId}
            handleSave={handleSave}
            onCancel={() => { setShowForm(false); setEditId(null); setFormError(""); }}
            dayOptions={dayOptions}
            saving={saving}
            formError={formError}
          />
        ) : (
          <Card key={c.id} style={{ padding: "14px 16px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 20 }}>{c.tipo === "efectivo" ? "💵" : c.tipo === "vales" ? "🎫" : c.tipo === "ahorro" ? "🐿️" : "💳"}</span>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF }}>{c.nombre}</div>
                    <div style={{ fontSize: 11, color: P.textSm, fontFamily: FF }}>
                      {TIPO_LABELS[c.tipo] || c.tipo}{c.banco ? ` · ${c.banco}` : ""}
                    </div>
                  </div>
                </div>
                {c.tipo === "crédito" && c.limite_credito && (
                  <div style={{ marginTop: 8, display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <MiniStat label="Límite"  value={`$${Number(c.limite_credito).toLocaleString()}`} />
                    {c.fecha_corte       && <MiniStat label="Corte" value={`Día ${c.fecha_corte}`} />}
                    {c.fecha_limite_pago && <MiniStat label="Pago"  value={`Día ${c.fecha_limite_pago}${Number(c.fecha_limite_pago) < Number(c.fecha_corte) ? " (sig. mes)" : ""}`} />}
                  </div>
                )}
                {c.rastrear_saldo && computedSaldos?.[c.id] != null && (
                  <div style={{ marginTop: 6, fontSize: 13, fontWeight: 700, color: P.ingresos.base, fontFamily: FF }}>
                    Saldo: ${Number(computedSaldos[c.id]).toLocaleString()}
                  </div>
                )}
              </div>

              {/* Botones de acción con confirmación inline */}
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {confirmDelete === c.id ? (
                  <>
                    <span style={{ fontSize: 11, color: P.textSm, fontFamily: FF }}>¿Seguro?</span>
                    <SmallBtn label="✓" onClick={() => handleDelete(c.id)} danger />
                    <SmallBtn label="✕" onClick={() => setConfirmDelete(null)} />
                  </>
                ) : (
                  <>
                    <SmallBtn label="✏️" onClick={() => handleEdit(c)} />
                    <SmallBtn label="🗑️" onClick={() => setConfirmDelete(c.id)} danger />
                  </>
                )}
              </div>
            </div>
          </Card>
        )
      ))}

      {/* Botón agregar / Form nueva cuenta (solo si no estamos editando) */}
      {!showForm || editId ? (
        <button type="button" onClick={() => { setForm(emptyAccountForm()); setEditId(null); setFormError(""); setShowForm(true); }}
          style={{ padding: "12px 18px", borderRadius: 14, border: `2px dashed ${P.border}`, background: "transparent", color: P.ingresos.base, fontFamily: FF, fontSize: 14, fontWeight: 700, cursor: "pointer" }}>
          + Agregar cuenta
        </button>
      ) : (
        <AccountForm
          form={form} setForm={setForm}
          editId={null}
          handleSave={handleSave}
          onCancel={() => { setShowForm(false); setEditId(null); setFormError(""); }}
          dayOptions={dayOptions}
          saving={saving}
          formError={formError}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   CategoriasSection — CRUD de categorías personalizables
   ═══════════════════════════════════════════════════════════════════ */
function CategoriasSection({ categorias, createCategoria, updateCategoria, deleteCategoria, mobile }) {
  const [showForm, setShowForm]     = useState(false);
  const [editId, setEditId]         = useState(null);
  const [form, setForm]             = useState({ nombre: "", icono: "📦", color: "#94A3B8" });
  const [saving, setSaving]         = useState(false);
  const [formError, setFormError]   = useState("");
  const [sectionError, setSectionError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  function showSectionError(msg) {
    setSectionError(msg);
    setTimeout(() => setSectionError(""), 3000);
  }

  const handleEdit = (cat) => {
    setForm({ nombre: cat.nombre, icono: cat.icono || "📦", color: cat.color || "#94A3B8" });
    setEditId(cat.id);
    setFormError("");
    setShowForm(true);
  };

  const handleSave = async () => {
    // Validaciones
    if (!form.nombre.trim()) {
      setFormError("El nombre es obligatorio");
      return;
    }
    // Duplicados (excluyendo el que se está editando)
    const nombreLower = form.nombre.trim().toLowerCase();
    const duplicado = categorias.find(
      (c) => c.nombre.toLowerCase() === nombreLower && c.id !== editId && c.activa !== false
    );
    if (duplicado) {
      setFormError("Ya existe una categoría con ese nombre");
      return;
    }
    setFormError("");

    setSaving(true);
    try {
      if (editId) {
        await updateCategoria(editId, { ...form, nombre: form.nombre.trim() });
      } else {
        await createCategoria({ ...form, nombre: form.nombre.trim() });
      }
      setShowForm(false);
      setEditId(null);
      setForm({ nombre: "", icono: "📦", color: "#94A3B8" });
    } catch (e) {
      showSectionError("Error al guardar: " + (e.message || "intenta de nuevo"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, nombre) => {
    if (nombre === "Otros") {
      showSectionError("La categoría 'Otros' no se puede eliminar.");
      setConfirmDelete(null);
      return;
    }
    try {
      await deleteCategoria(id);
      setConfirmDelete(null);
    } catch (e) {
      showSectionError("Error al eliminar: " + (e.message || "intenta de nuevo"));
      setConfirmDelete(null);
    }
  };

  const renderForm = () => (
    <>
      <div style={{ fontSize: 14, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 12 }}>
        {editId ? "Editar categoría" : "Nueva categoría"}
      </div>

      {/* Error inline */}
      {formError && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#DC2626", fontFamily: FF, fontWeight: 600 }}>
          {formError}
        </div>
      )}

      <FormField
        label="Nombre"
        htmlFor="cat-nombre"
        value={form.nombre}
        onChange={(v) => setForm({ ...form, nombre: v })}
        placeholder="Ej: Videojuegos"
      />

      {/* Icon picker */}
      <div style={{ marginBottom: 10 }}>
        <label htmlFor="cat-icono" style={labelStyle}>Icono</label>
        <div id="cat-icono" style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: "100%" }}>
          {ICON_OPTIONS.map((icon) => (
            <button key={icon} onClick={() => setForm({ ...form, icono: icon })}
              style={{ width: 34, height: 34, borderRadius: 8, border: `2px solid ${form.icono === icon ? P.ingresos.base : "transparent"}`, background: form.icono === icon ? P.ingresos.pastel : P.bg, fontSize: 16, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {icon}
            </button>
          ))}
        </div>
      </div>

      {/* Color picker */}
      <div style={{ marginBottom: 10 }}>
        <label htmlFor="cat-color" style={labelStyle}>Color</label>
        <div id="cat-color" style={{ display: "flex", flexWrap: "wrap", gap: 4, maxWidth: "100%" }}>
          {COLOR_OPTIONS.map((color) => (
            <button key={color} onClick={() => setForm({ ...form, color })}
              style={{ width: 28, height: 28, borderRadius: 8, border: `2.5px solid ${form.color === color ? P.text : "transparent"}`, background: color, cursor: "pointer" }} />
          ))}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
        <button onClick={handleSave} disabled={saving}
          style={{ flex: 1, padding: "10px 16px", borderRadius: 12, border: "none", background: P.ingresos.base, color: "#fff", fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: saving ? "default" : "pointer", opacity: saving ? 0.7 : 1 }}>
          {saving ? "Guardando..." : editId ? "Guardar" : "Agregar"}
        </button>
        <button onClick={() => { setShowForm(false); setEditId(null); setFormError(""); }}
          style={{ padding: "10px 16px", borderRadius: 12, border: `1.5px solid ${P.border}`, background: "transparent", color: P.textSm, fontFamily: FF, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
          Cancelar
        </button>
      </div>
    </>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, overflow: "hidden", maxWidth: "100%" }}>
      {/* Error de sección */}
      {sectionError && (
        <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#DC2626", fontFamily: FF, fontWeight: 600 }}>
          {sectionError}
        </div>
      )}

      {/* Grid de categorías */}
      <div style={{ display: "grid", gridTemplateColumns: mobile ? "1fr" : "1fr 1fr 1fr", gap: 8, overflow: "hidden" }}>
        {categorias.filter((c) => c.activa !== false).map((cat) => {
          // Edit-in-place: render form inside this slot
          if (showForm && editId === cat.id) {
            return (
              <Card key={cat.id} style={{ padding: 16, overflow: "hidden", gridColumn: mobile ? "auto" : "1 / -1" }}>
                {renderForm()}
              </Card>
            );
          }
          return (
            <Card key={cat.id} style={{ padding: "10px 12px", display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: 10, background: `${cat.color}20`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                {cat.icono || "📦"}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: P.text, fontFamily: FF, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {cat.nombre}
                </div>
                <div style={{ width: 14, height: 4, borderRadius: 2, background: cat.color, marginTop: 3 }} />
              </div>
              {/* Confirmación inline */}
              <div style={{ display: "flex", gap: 2, flexShrink: 0, alignItems: "center" }}>
                {confirmDelete === cat.id ? (
                  <>
                    <span style={{ fontSize: 10, color: P.textSm, fontFamily: FF }}>¿Seguro?</span>
                    <SmallBtn label="✓" onClick={() => handleDelete(cat.id, cat.nombre)} danger />
                    <SmallBtn label="✕" onClick={() => setConfirmDelete(null)} />
                  </>
                ) : (
                  <>
                    <SmallBtn label="✏️" onClick={() => handleEdit(cat)} />
                    {cat.nombre !== "Otros" && <SmallBtn label="×" onClick={() => setConfirmDelete(cat.id)} danger />}
                  </>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Agregar / Form para nueva categoría (solo cuando NO se está editando) */}
      {!showForm ? (
        <button onClick={() => { setForm({ nombre: "", icono: "📦", color: "#94A3B8" }); setEditId(null); setFormError(""); setShowForm(true); }}
          style={{ padding: "10px 16px", borderRadius: 14, border: `2px dashed ${P.border}`, background: "transparent", color: P.ingresos.base, fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
          + Agregar categoría
        </button>
      ) : !editId ? (
        <Card style={{ padding: 16, overflow: "hidden" }}>
          {renderForm()}
        </Card>
      ) : null}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   PerfilSection — Nombre, día de cobro, legal, cerrar sesión
   ═══════════════════════════════════════════════════════════════════ */
function PerfilSection({ diaCobro, setDiaCobro }) {
  const { signOut, user } = useAuth();
  const { nombreUsuario, setNombreUsuario } = useFinancial();
  const { setView } = useNavigation();

  const [localNombre, setLocalNombre] = useState(nombreUsuario || "");
  const [nombreSaved, setNombreSaved] = useState(false);
  const [nombreError, setNombreError] = useState("");

  const [localDia, setLocalDia] = useState(diaCobro || "15");
  const [diaSaved, setDiaSaved] = useState(false);
  const [diaError, setDiaError] = useState("");

  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => { setLocalNombre(nombreUsuario || ""); }, [nombreUsuario]);
  useEffect(() => { setLocalDia(diaCobro || "15"); }, [diaCobro]);

  const dayOptions = Array.from({ length: 31 }, (_, i) => i + 1);

  const handleSaveNombre = async () => {
    if (!localNombre.trim()) {
      setNombreError("El nombre es obligatorio");
      setTimeout(() => setNombreError(""), 3000);
      return;
    }
    try {
      await api.preferencias.upsert("nombre_usuario", localNombre.trim());
      setNombreUsuario(localNombre.trim());
      setNombreSaved(true);
      setTimeout(() => setNombreSaved(false), 2000);
    } catch (e) {
      setNombreError("Error al guardar: " + (e.message || "intenta de nuevo"));
      setTimeout(() => setNombreError(""), 3000);
    }
  };

  const handleSaveDia = async () => {
    try {
      await api.preferencias.upsert("dia_cobro", localDia);
      setDiaCobro(Number(localDia));
      setDiaSaved(true);
      setTimeout(() => setDiaSaved(false), 2000);
    } catch (e) {
      setDiaError("Error al guardar: " + (e.message || "intenta de nuevo"));
      setTimeout(() => setDiaError(""), 3000);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* Nombre de usuario */}
      <Card style={{ padding: "18px 20px" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 14 }}>👤 Tu perfil</div>

        {nombreError && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#DC2626", fontFamily: FF, fontWeight: 600 }}>
            {nombreError}
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <label htmlFor="perfil-nombre" style={labelStyle}>Nombre</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              id="perfil-nombre"
              value={localNombre}
              onChange={(e) => setLocalNombre(e.target.value)}
              placeholder="Tu nombre"
              style={{ ...inputBase, flex: 1 }}
            />
            <button
              onClick={handleSaveNombre}
              style={{
                padding: "10px 16px", borderRadius: 12, border: "none",
                background: nombreSaved ? "#10B981" : P.ingresos.base,
                color: "#fff", fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: "pointer",
                transition: "background .2s", whiteSpace: "nowrap",
              }}
            >
              {nombreSaved ? "✓ Guardado" : "Guardar"}
            </button>
          </div>
        </div>

        {user?.email && (
          <div style={{ marginBottom: 4 }}>
            <label style={labelStyle}>Email</label>
            <div style={{ fontSize: 13, color: P.textSm, fontFamily: FF, padding: "8px 0" }}>{user.email}</div>
          </div>
        )}
      </Card>

      {/* Día de cobro */}
      <Card style={{ padding: "18px 20px" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 4 }}>📅 Día de cobro</div>
        <div style={{ fontSize: 12, color: P.textSm, fontFamily: FF, marginBottom: 14, lineHeight: 1.5 }}>
          Indica qué día del mes recibes tu ingreso principal. Esto permite calcular tus métricas "desde cobro" en el dashboard.
        </div>

        {diaError && (
          <div style={{ background: "#FEF2F2", border: "1px solid #FCA5A5", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 12, color: "#DC2626", fontFamily: FF, fontWeight: 600 }}>
            {diaError}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <label htmlFor="perfil-dia" style={{ display: "none" }}>Día de cobro</label>
          <select
            id="perfil-dia"
            value={localDia}
            onChange={(e) => setLocalDia(e.target.value)}
            style={{ ...inputBase, width: 100, appearance: "auto", fontSize: 14, fontWeight: 700, textAlign: "center" }}
          >
            {dayOptions.map((d) => (
              <option key={d} value={d}>Día {d}</option>
            ))}
          </select>
          <button
            onClick={handleSaveDia}
            style={{
              padding: "10px 20px", borderRadius: 12, border: "none",
              background: diaSaved ? "#10B981" : P.ingresos.base,
              color: "#fff", fontFamily: FF, fontSize: 13, fontWeight: 700, cursor: "pointer",
              transition: "background .2s",
            }}
          >
            {diaSaved ? "✓ Guardado" : "Guardar"}
          </button>
        </div>
      </Card>

      {/* Notificaciones push */}
      <NotificationsConfig />

      {/* Legal */}
      <Card style={{ padding: "18px 20px" }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: P.text, fontFamily: FF, marginBottom: 12 }}>📋 Legal</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button onClick={() => setView("terminos")}
            style={{ fontSize: 13, color: P.ingresos.base, fontFamily: FF, fontWeight: 600, textDecoration: "none", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
            Terminos de servicio →
          </button>
          <button onClick={() => setView("privacidad")}
            style={{ fontSize: 13, color: P.ingresos.base, fontFamily: FF, fontWeight: 600, textDecoration: "none", background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
            Aviso de privacidad →
          </button>
        </div>
      </Card>

      {/* Cerrar sesión */}
      <Card style={{ padding: "18px 20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: P.text, fontFamily: FF }}>🚪 Cerrar sesión</div>
            {user?.email && <div style={{ fontSize: 11, color: P.textXs, fontFamily: FF, marginTop: 2 }}>{user.email}</div>}
          </div>
          <button
            onClick={async () => { setLoggingOut(true); try { await signOut(); } catch {} }}
            disabled={loggingOut}
            style={{
              padding: "10px 20px", borderRadius: 12, border: `1.5px solid ${P.danger?.tint || "#FCA5A5"}`,
              background: P.danger?.pastel || "#FEF2F2", color: P.danger?.text || "#DC2626", fontFamily: FF,
              fontSize: 13, fontWeight: 700, cursor: loggingOut ? "default" : "pointer",
              opacity: loggingOut ? 0.6 : 1,
            }}
          >
            {loggingOut ? "Saliendo..." : "Cerrar sesión"}
          </button>
        </div>
      </Card>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Helpers / small components
   ═══════════════════════════════════════════════════════════════════ */
const labelStyle = {
  display: "block", fontSize: 11, fontWeight: 700, color: P.textSm,
  fontFamily: FF, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4,
};

function FormField({ label, htmlFor, value, onChange, placeholder, inputMode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label htmlFor={htmlFor} style={labelStyle}>{label}</label>
      <input
        id={htmlFor}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        inputMode={inputMode}
        style={inputBase}
      />
    </div>
  );
}

function SmallBtn({ label, onClick, danger }) {
  return (
    <button onClick={onClick}
      style={{
        width: 44, height: 44, borderRadius: 10,
        border: `1px solid ${danger ? "#FCA5A5" : P.border}`,
        background: danger ? "#FEF2F2" : P.bg,
        fontSize: 14, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center",
        color: danger ? "#DC2626" : P.textSm,
        minWidth: 44,
      }}>
      {label}
    </button>
  );
}

function MiniStat({ label, value }) {
  return (
    <div style={{ background: P.bg, borderRadius: 8, padding: "4px 10px" }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: P.textXs, textTransform: "uppercase", fontFamily: FF }}>{label}</div>
      <div style={{ fontSize: 12, fontWeight: 800, color: P.text, fontFamily: FF }}>{value}</div>
    </div>
  );
}

function MiniStatDark({ label, value }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.08)", borderRadius: 8, padding: "4px 10px" }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.45)", textTransform: "uppercase", fontFamily: FF }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 800, color: "#fff", fontFamily: FF }}>{value}</div>
    </div>
  );
}
